/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { FileText, RefreshCw } from 'lucide-react';
import { SkillShopService } from '../../services/skillShopService';

/**
 * ChangelogSettings component
 * 
 * Displays the Velocity & SkillShop changelog with formatted markdown rendering.
 */
export function ChangelogSettings() {
  const [skillShopService] = useState(() => new SkillShopService());
  const [changelogContent, setChangelogContent] = useState<string>('');
  const [isLoadingChangelog, setIsLoadingChangelog] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load changelog on mount
  useEffect(() => {
    if (!hasLoaded && !isLoadingChangelog) {
      setIsLoadingChangelog(true);
      setHasLoaded(true);
      skillShopService.getChangelog()
        .then((result) => {
          if (result?.content) {
            setChangelogContent(result.content);
          }
        })
        .catch((error) => {
          console.error('Failed to load changelog:', error);
        })
        .finally(() => {
          setIsLoadingChangelog(false);
        });
    }
  }, [hasLoaded, isLoadingChangelog, skillShopService]);

  return (
    <div style={{ 
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <FileText size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>Velocity & SkillShop Changelog</h3>
      </div>
      
      {isLoadingChangelog ? (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '40px',
          color: '#6b7280'
        }}>
          <RefreshCw size={20} className="spin" style={{ marginRight: '8px' }} />
          Loading changelog...
        </div>
      ) : changelogContent ? (
        <div style={{
          maxHeight: '600px',
          overflowY: 'auto',
          padding: '16px',
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #e5e7eb',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
          fontSize: '13px',
          lineHeight: '1.6'
        }}>
          {changelogContent.split('\n').map((line, index) => {
            // Version headers like "## [0.7.5] - 2026-01-23"
            if (line.match(/^## \[[\d.]+\]/)) {
              return (
                <div key={index} style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1e40af',
                  marginTop: index === 0 ? '0' : '24px',
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '2px solid #3b82f6'
                }}>
                  {line.replace(/^## /, '')}
                </div>
              );
            }
            
            // Section headers like "### Velocity PPM"
            if (line.match(/^### /)) {
              return (
                <div key={index} style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#0d9488',
                  marginTop: '16px',
                  marginBottom: '8px'
                }}>
                  {line.replace(/^### /, '')}
                </div>
              );
            }
            
            // Subheaders like "#### Something"
            if (line.match(/^#### /)) {
              return (
                <div key={index} style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#6366f1',
                  marginTop: '12px',
                  marginBottom: '6px'
                }}>
                  {line.replace(/^#### /, '')}
                </div>
              );
            }
            
            // Main title
            if (line.match(/^# /)) {
              return (
                <div key={index} style={{
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: '16px'
                }}>
                  {line.replace(/^# /, '')}
                </div>
              );
            }
            
            // Feature entries [Feature]
            if (line.includes('[Feature]')) {
              return (
                <div key={index} style={{
                  padding: '4px 0',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <span style={{
                    background: '#dcfce7',
                    color: '#166534',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    flexShrink: 0
                  }}>Feature</span>
                  <span style={{ color: '#374151' }}>
                    {line.replace(/\[Feature\]\s*/, '')}
                  </span>
                </div>
              );
            }
            
            // Bug fix entries [Bug Fix]
            if (line.includes('[Bug Fix]')) {
              return (
                <div key={index} style={{
                  padding: '4px 0',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <span style={{
                    background: '#fee2e2',
                    color: '#991b1b',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    flexShrink: 0
                  }}>Bug Fix</span>
                  <span style={{ color: '#374151' }}>
                    {line.replace(/\[Bug Fix\]\s*/, '')}
                  </span>
                </div>
              );
            }
            
            // Task entries [Task]
            if (line.includes('[Task]')) {
              return (
                <div key={index} style={{
                  padding: '4px 0',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <span style={{
                    background: '#e0e7ff',
                    color: '#3730a3',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    flexShrink: 0
                  }}>Task</span>
                  <span style={{ color: '#374151' }}>
                    {line.replace(/\[Task\]\s*/, '')}
                  </span>
                </div>
              );
            }
            
            // List items starting with - or *
            if (line.match(/^[\s]*[-*]\s+\*\*/)) {
              // Bold list item like "- **Feature Name**: Description"
              const parts = line.replace(/^[\s]*[-*]\s+/, '');
              return (
                <div key={index} style={{
                  padding: '3px 0',
                  paddingLeft: '16px',
                  color: '#374151'
                }} dangerouslySetInnerHTML={{
                  __html: parts.replace(/\*\*([^*]+)\*\*/g, '<strong style="color: #1f2937">$1</strong>')
                }} />
              );
            }
            
            if (line.match(/^[\s]*[-*]\s+/)) {
              return (
                <div key={index} style={{
                  padding: '3px 0',
                  paddingLeft: '16px',
                  color: '#4b5563'
                }}>
                  {line.replace(/^[\s]*[-*]\s+/, '• ')}
                </div>
              );
            }
            
            // Numbered list items
            if (line.match(/^\s*\d+\.\s+/)) {
              return (
                <div key={index} style={{
                  padding: '3px 0',
                  paddingLeft: '16px',
                  color: '#4b5563'
                }}>
                  {line}
                </div>
              );
            }
            
            // Empty lines
            if (line.trim() === '') {
              return <div key={index} style={{ height: '8px' }} />;
            }
            
            // Code blocks (```...```)
            if (line.trim().startsWith('```')) {
              return (
                <div key={index} style={{
                  background: '#1f2937',
                  color: '#e5e7eb',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}>
                  {line.trim() === '```' ? '' : line.replace(/^```\w*/, '')}
                </div>
              );
            }
            
            // Links at the bottom
            if (line.match(/^\[[\d.]+\]:/)) {
              return (
                <div key={index} style={{
                  fontSize: '11px',
                  color: '#9ca3af',
                  padding: '2px 0'
                }}>
                  {line}
                </div>
              );
            }
            
            // Regular text
            return (
              <div key={index} style={{
                padding: '2px 0',
                color: '#4b5563'
              }}>
                {line}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#6b7280'
        }}>
          Unable to load changelog
        </div>
      )}
    </div>
  );
}
